<script setup>
import { ref, computed, watch, onBeforeUnmount } from "vue"
import { useRouter } from "vue-router"
import {
  Leaf, Menu, X, LogOut, MonitorX, Inbox, SlidersHorizontal, Link2, Users, Settings,
  ShieldCheck, Trash2, Activity, MonitorSmartphone, CheckCircle2, Info, AlertTriangle, ChevronDown, Bell, KeyRound, LayoutDashboard, Wrench,
} from "lucide-vue-next"
import { adminLoggedIn, nLoggedIn, nUser, adminPermissions, clearTokens, canGetAdminAccess } from "./store.js"
import { API } from "./api.js"
import { toast, toasts, dismissToast, holdToast, releaseToast } from "./utils.js"
import { cn } from "@/lib/utils"
import { ask } from "@/lib/confirm"
import ConfirmDialog from "@/components/ConfirmDialog.vue"

const toastClass = (t) => cn("flex items-start gap-2.5 rounded-xl border shadow-lg px-4 py-3 text-sm bg-card",
  t.type === "success" ? "border-primary/30 text-primary"
  : t.type === "info" ? "border-blue-200 text-blue-700"
  : "border-destructive/30 text-destructive")

const ADMIN_SECTIONS = [
  { to: "/admin/submissions",   perm: "perm_submissions",   icon: Inbox,             label: "Ответы" },
  { to: "/admin/questions",     perm: "perm_questions",     icon: SlidersHorizontal, label: "Вопросы" },
  { to: "/admin/invitations",   perm: "perm_invitations",   icon: Link2,             label: "Ссылки" },
  { to: "/admin/nutritionists", perm: "perm_nutritionists", icon: Users,             label: "Нутрициологи" },
  { to: "/admin/settings",      perm: "perm_settings",      icon: Settings,          label: "Настройки" },
  { to: "/admin/roles",         perm: "perm_settings",      icon: ShieldCheck,       label: "Роли" },
  { to: "/admin/trash",         perm: "perm_trash",         icon: Trash2,            label: "Корзина" },
  { to: "/admin/monitoring",    superAdmin: true,           icon: Activity,          label: "Мониторинг" },
]
// Cabinet sections live as tabs on /n/dashboard — surface them as top-level nav.
const CABINET_SECTIONS = [
  { tab: "overview",    icon: LayoutDashboard, label: "Обзор" },
  { tab: "clients",     icon: Users,  label: "Клиенты" },
  { tab: "invitations", icon: Link2,  label: "Ссылки" },
  { tab: "submissions", icon: Inbox,  label: "Анкеты" },
]
const ACCOUNT_SECTIONS = [
  { tab: "notifications", icon: Bell,     label: "Уведомления" },
  { tab: "sessions",      icon: MonitorSmartphone, label: "Сеансы" },
  { tab: "access",        icon: KeyRound, label: "Доступ" },
]

const router = useRouter()
const route = router.currentRoute
const menuOpen = ref(false)
const accountOpen = ref(false)
const accountEl = ref(null)
const adminOpen = ref(false)
const adminEl = ref(null)

const cabinetLinks = computed(() => (nLoggedIn.value ? CABINET_SECTIONS : []))
const adminLinks = computed(() =>
  ADMIN_SECTIONS.filter((s) => adminLoggedIn.value || (!s.superAdmin && adminPermissions.value[s.perm])))
const accountLinks = computed(() => (nLoggedIn.value
  ? ACCOUNT_SECTIONS.filter((l) => l.tab !== "access" || canGetAdminAccess.value)
  : []))
const showNav = computed(() => adminLoggedIn.value || nLoggedIn.value)

// The logo/home target depends on who's signed in.
const homeTo = computed(() => {
  if (nLoggedIn.value) return { path: "/n/dashboard", query: { tab: "overview" } }
  if (adminLoggedIn.value) return (adminLinks.value[0]?.to || "/admin/submissions")
  return "/"
})
// Clicking the logo should always feel responsive — even when already "home"
// (router-link skips navigation to the same route), scroll back to the top.
const onHome = () => window.scrollTo({ top: 0, behavior: "smooth" })

const dashTo = (tab) => ({ path: "/n/dashboard", query: { tab } })
const cabinetActive = (tab) =>
  route.value.path === "/n/dashboard" && (route.value.query.tab || "overview") === tab
const isActive = (to) => route.value.path === to || route.value.path.startsWith(to + "/")
const adminSectionActive = computed(() => adminLinks.value.some((l) => isActive(l.to)))
// A super-admin has no cabinet links, so the sections fit inline: all eight
// from xl (1280px); at lg only up to seven, so eight collapse behind the menu
// below xl. Alongside the cabinet links they'd need ~1350px: always the menu.
const groupAdminLinks = computed(() => cabinetLinks.value.length > 0)
const crowdedAdminLinks = computed(() => !groupAdminLinks.value && adminLinks.value.length > 7)

watch(() => route.value.fullPath, () => { menuOpen.value = false; accountOpen.value = false; adminOpen.value = false })

const onDocClick = (e) => {
  if (accountEl.value && !accountEl.value.contains(e.target)) accountOpen.value = false
  if (adminEl.value && !adminEl.value.contains(e.target)) adminOpen.value = false
}
document.addEventListener("click", onDocClick)
// Escape closes an open menu and returns focus to its button (keyboard users
// were left in a menu that only a mouse click elsewhere could close).
const closeMenu = (which, e) => {
  if (!which) return
  if (which === "admin") adminOpen.value = false
  else accountOpen.value = false
  e.currentTarget.querySelector("button")?.focus()
}
onBeforeUnmount(() => document.removeEventListener("click", onDocClick))

// What this deployment actually is. Shown only behind the nav, so public form
// pages stay uncluttered; a failed fetch just leaves the footer off.
const buildInfo = ref(null)
watch(showNav, async (shown) => {
  if (!shown || buildInfo.value) return
  try { buildInfo.value = await API.get("/api/version") } catch { /* not worth surfacing */ }
}, { immediate: true })

const endCabinetSession = () => { clearTokens(); nLoggedIn.value = false; nUser.value = null; router.push("/n") }
const logout = async () => {
  menuOpen.value = false; accountOpen.value = false
  if (nLoggedIn.value) {
    // Drop local tokens even if the request fails — shared devices must not stay signed in.
    try { await API.post("/api/n/logout") } catch { /* already logged out server-side */ }
    endCabinetSession()
  } else {
    await API.post("/api/admin/logout"); adminLoggedIn.value = false; router.push("/admin")
  }
}
const logoutEverywhere = async () => {
  menuOpen.value = false; accountOpen.value = false
  if (!(await ask({ title: "Выйти на всех устройствах?", message: "Потребуется войти заново везде, включая это устройство.", confirmLabel: "Выйти везде" }))) return
  try { await API.post("/api/n/logout-all") } catch (e) { toast("Не удалось завершить сеансы: " + e.message); return }
  endCabinetSession()
}
</script>

<template>
  <div class="min-h-screen">
    <nav v-if="showNav" class="sticky top-0 z-20 bg-primary text-primary-foreground shadow-sm">
      <div class="mx-auto max-w-6xl px-4 h-14 flex items-center justify-between gap-3">
        <router-link :to="homeTo" @click="onHome" class="font-semibold tracking-tight flex items-center gap-2 whitespace-nowrap shrink-0">
          <Leaf class="h-5 w-5" /> NSP Forms
        </router-link>

        <!-- lg, not md: even with the admin sections collapsed the worst-case row
             (4 cabinet links + the menu + the account button) needs ~880px, so at
             the md breakpoint it still overflowed a 768px tablet. -->
        <div class="hidden lg:flex items-center gap-1 text-sm">
          <router-link v-for="l in cabinetLinks" :key="l.tab" :to="dashTo(l.tab)"
            :class="cn('px-2.5 py-1.5 rounded-lg transition flex items-center gap-1.5 whitespace-nowrap',
              cabinetActive(l.tab) ? 'bg-black/15 font-medium' : 'opacity-90 hover:opacity-100 hover:bg-black/10')">
            <component :is="l.icon" class="h-4 w-4" /> {{ l.label }}
          </router-link>

          <span v-if="cabinetLinks.length && adminLinks.length" class="mx-1 h-5 w-px bg-white/20"></span>

          <!-- Inline for an admin-only user, where they fit. For a user who also has the
               cabinet links the combined row needed ~1350px and pushed the whole page
               sideways at 1280, so there the seven sections collapse behind one menu.
               The hamburger menu below always lists every section flat. -->
          <div v-if="adminLinks.length && !groupAdminLinks" :class="cn('items-center gap-1', crowdedAdminLinks ? 'hidden xl:flex' : 'flex')">
            <router-link v-for="l in adminLinks" :key="l.to" :to="l.to"
              :class="cn('px-2.5 py-1.5 rounded-lg transition flex items-center gap-1.5 whitespace-nowrap',
                isActive(l.to) ? 'bg-black/15 font-medium' : 'opacity-90 hover:opacity-100 hover:bg-black/10')">
              <component :is="l.icon" class="h-4 w-4" /> {{ l.label }}
            </router-link>
          </div>
          <div v-if="adminLinks.length && (groupAdminLinks || crowdedAdminLinks)" ref="adminEl"
            :class="cn('relative', crowdedAdminLinks && 'xl:hidden')" @keydown.escape="closeMenu(adminOpen ? 'admin' : null, $event)">
            <button @click="adminOpen = !adminOpen" :aria-expanded="adminOpen"
              :class="cn('px-2.5 py-1.5 rounded-lg transition flex items-center gap-1.5 whitespace-nowrap',
                adminSectionActive ? 'bg-black/15 font-medium' : 'opacity-90 hover:opacity-100 hover:bg-black/10')">
              <Wrench class="h-4 w-4" /> Админ
              <ChevronDown class="h-3.5 w-3.5" :class="adminOpen ? 'rotate-180' : ''" />
            </button>
            <div v-if="adminOpen" class="absolute left-0 z-30 mt-1 w-56 rounded-xl border bg-card text-foreground shadow-xl overflow-hidden py-1">
              <router-link v-for="l in adminLinks" :key="l.to" :to="l.to"
                :class="cn('flex items-center gap-2.5 px-3 py-2 text-sm transition hover:bg-secondary',
                  isActive(l.to) ? 'bg-secondary font-medium' : '')">
                <component :is="l.icon" class="h-4 w-4 text-muted-foreground" /> {{ l.label }}
              </router-link>
            </div>
          </div>

          <!-- account -->
          <div v-if="nLoggedIn" ref="accountEl" class="relative ml-1" @keydown.escape="closeMenu(accountOpen ? 'account' : null, $event)">
            <button @click="accountOpen = !accountOpen" :aria-expanded="accountOpen"
              class="px-2.5 py-1.5 rounded-lg opacity-90 hover:opacity-100 hover:bg-black/10 transition flex items-center gap-1.5">
              <span class="grid h-6 w-6 place-items-center rounded-full bg-black/15 text-xs">{{ (nUser?.display_name || "?").slice(0, 1) }}</span>
              <span class="max-w-[10rem] truncate">{{ nUser?.display_name || "Кабинет" }}</span>
              <ChevronDown class="h-3.5 w-3.5" :class="accountOpen ? 'rotate-180' : ''" />
            </button>
            <div v-if="accountOpen" class="absolute right-0 mt-1 w-52 rounded-xl border bg-card text-foreground shadow-xl overflow-hidden py-1">
              <router-link v-for="l in accountLinks" :key="l.tab" :to="dashTo(l.tab)"
                class="flex items-center gap-2.5 px-3 py-2 text-sm hover:bg-secondary transition">
                <component :is="l.icon" class="h-4 w-4 text-muted-foreground" /> {{ l.label }}
              </router-link>
              <div class="my-1 border-t"></div>
              <button @click="logoutEverywhere" class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-secondary transition">
                <MonitorX class="h-4 w-4 text-muted-foreground" /> Выйти на всех устройствах
              </button>
              <button @click="logout" class="w-full flex items-center gap-2.5 px-3 py-2 text-sm text-left hover:bg-secondary transition text-destructive">
                <LogOut class="h-4 w-4" /> Выйти
              </button>
            </div>
          </div>
          <button v-else @click="logout"
            class="ml-1 px-2.5 py-1.5 rounded-lg opacity-90 hover:opacity-100 hover:bg-black/10 transition flex items-center gap-1.5">
            <LogOut class="h-4 w-4" /> Выйти
          </button>
        </div>

        <button @click="menuOpen = !menuOpen" @keydown.escape="menuOpen = false" class="lg:hidden p-1 -mr-1" aria-label="Меню" :aria-expanded="menuOpen">
          <component :is="menuOpen ? X : Menu" class="h-6 w-6" />
        </button>
      </div>

      <div v-if="menuOpen" class="lg:hidden border-t border-white/15 px-2 py-2 flex flex-col gap-0.5">
        <router-link v-for="l in cabinetLinks" :key="l.tab" :to="dashTo(l.tab)"
          :class="cn('px-3 py-2.5 rounded-lg transition flex items-center gap-2.5',
            cabinetActive(l.tab) ? 'bg-black/15 font-medium' : 'hover:bg-black/10')">
          <component :is="l.icon" class="h-4.5 w-4.5" /> {{ l.label }}
        </router-link>
        <router-link v-for="l in accountLinks" :key="l.tab" :to="dashTo(l.tab)"
          class="px-3 py-2.5 rounded-lg transition flex items-center gap-2.5 hover:bg-black/10">
          <component :is="l.icon" class="h-4.5 w-4.5" /> {{ l.label }}
        </router-link>
        <div v-if="adminLinks.length" class="my-1 border-t border-white/15"></div>
        <router-link v-for="l in adminLinks" :key="l.to" :to="l.to"
          :class="cn('px-3 py-2.5 rounded-lg transition flex items-center gap-2.5',
            isActive(l.to) ? 'bg-black/15 font-medium' : 'hover:bg-black/10')">
          <component :is="l.icon" class="h-4.5 w-4.5" /> {{ l.label }}
        </router-link>
        <button v-if="nLoggedIn" @click="logoutEverywhere" class="px-3 py-2.5 rounded-lg hover:bg-black/10 transition flex items-center gap-2.5 text-left">
          <MonitorX class="h-4.5 w-4.5" /> Выйти на всех устройствах
        </button>
        <button @click="logout" class="px-3 py-2.5 rounded-lg hover:bg-black/10 transition flex items-center gap-2.5 text-left">
          <LogOut class="h-4.5 w-4.5" /> Выйти
        </button>
      </div>
    </nav>

    <main>
      <router-view v-slot="{ Component, route: current }">
        <!-- Keyed on the signed-in nutritionist: logging out and in as someone
             else drops the cached Dashboard instead of showing the previous
             person's clients until the silent reload lands. -->
        <!-- The page is keyed on the path: /n/clients/1 → /n/clients/2 (a family
             member) is the same route, so Vue reused the page and kept showing,
             and acting on, the previous client. Query changes (tabs) don't
             remount. No comment inside <keep-alive>: in dev builds it counts as
             a second child and KeepAlive stops caching. -->
        <keep-alive include="Dashboard" :key="nUser?.id ?? 'signed-out'">
          <component :is="Component" :key="current.path" />
        </keep-alive>
      </router-view>
    </main>

    <!-- Which build is running. Operators only — hidden on the public forms. -->
    <footer v-if="showNav && buildInfo" class="mx-auto max-w-6xl px-4 pb-6 pt-2 text-center text-xs text-muted-foreground">
      <span :title="`сборка ${buildInfo.built}`">
        NSP Forms {{ buildInfo.version }}
        <!-- One interpolation: the template compiler drops spaces between and around elements. -->
        <span class="opacity-60">· {{ [buildInfo.branch, buildInfo.commit.slice(0, 7)].filter(Boolean).join(" · ") }}</span>
      </span>
    </footer>

    <!-- Toasts -->
    <ConfirmDialog />

    <!-- Live regions, always in the DOM so screen readers announce what is added:
         errors assertively, the rest politely. Toasts are how most failures are
         reported, and they used to appear and vanish in silence. -->
    <div class="fixed bottom-4 right-4 z-[60] flex flex-col gap-2 max-w-sm">
      <div role="alert" aria-live="assertive" class="contents">
        <template v-for="t in toasts" :key="t.id">
          <div v-if="t.type === 'error'" :class="toastClass(t)" @mouseenter="holdToast(t.id)" @mouseleave="releaseToast(t.id)" @focusin="holdToast(t.id)" @focusout="releaseToast(t.id)">
            <AlertTriangle class="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
            <span class="break-words flex-1">{{ t.message }}</span>
            <button type="button" class="-m-1 p-1 rounded opacity-70 hover:opacity-100" aria-label="Закрыть уведомление" @click="dismissToast(t.id)"><X class="h-3.5 w-3.5" /></button>
          </div>
        </template>
      </div>
      <div role="status" aria-live="polite" class="contents">
        <template v-for="t in toasts" :key="t.id">
          <div v-if="t.type !== 'error'" :class="toastClass(t)" @mouseenter="holdToast(t.id)" @mouseleave="releaseToast(t.id)" @focusin="holdToast(t.id)" @focusout="releaseToast(t.id)">
            <component :is="t.type === 'success' ? CheckCircle2 : Info" class="h-4 w-4 mt-0.5 shrink-0" aria-hidden="true" />
            <span class="break-words flex-1">{{ t.message }}</span>
            <button type="button" class="-m-1 p-1 rounded opacity-70 hover:opacity-100" aria-label="Закрыть уведомление" @click="dismissToast(t.id)"><X class="h-3.5 w-3.5" /></button>
          </div>
        </template>
      </div>
    </div>
  </div>
</template>